import React, { Suspense, lazy } from 'react';
import { Box, CircularProgress } from '@mui/material';

const load = () =>
  import(
    /* webpackChunkName: "customer-section" */
    /* webpackPrefetch: true */
    './CustomerSectionPage'
  );

const CustomerSectionPage = lazy(load);

/**
 * Loads the workspace's code without rendering it.
 *
 * Prefetching only puts the chunk in the cache; its modules are evaluated by the first import, and
 * that evaluation - with the style sheets the components build as they first render - is what a
 * customer being opened otherwise waits for. Calling this while the browser is idle moves that cost
 * off the click. The promise is the one `lazy` awaits, so calling it twice loads nothing twice.
 *
 * @returns {Promise}
 */
export const preloadCustomerSectionPage = load;

/**
 * `CustomerSectionPage` behind a chunk boundary.
 *
 * It is the heavy half of the customer workspace — CallSection, DetailsSection,
 * EmailSection, ChatSection and the history/progress tabs — and it is reached
 * from two overlays (`CustomerPage/CustomerComponents` and
 * `LeadsManagement/Leads/Components/LeadComponents`) that are mounted on every
 * authenticated page, whether or not a customer is open.
 *
 * Both overlays already render it only behind a selected record, so deferring
 * the code changes nothing about when it appears. What changes is that the code
 * is no longer downloaded and parsed by users who never open one.
 *
 * The wrapper exists so that both call sites keep importing a plain component
 * and one chunk serves both. The overlays themselves stay eagerly mounted: they
 * hold the after-call and notification effects, which must keep running when no
 * customer is selected.
 *
 * `webpackPrefetch` has the browser fetch the chunk while it is idle, rather
 * than when a customer is opened: the code is large enough that downloading and
 * parsing it on demand is the whole of the wait before the workspace appears,
 * and it is only ever paid once. Until it arrives the fallback says so, since an
 * overlay that opens onto nothing reads as a failure rather than as loading.
 */
const LazyCustomerSectionPage = function LazyCustomerSectionPage(props) {
  return (
    <Suspense
      fallback={
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      }
    >
      <CustomerSectionPage {...props} />
    </Suspense>
  );
};

export default LazyCustomerSectionPage;
